// The rail's camera group: turn, tilt and fit, as a list.
//
// **Every camera action is a control before it is a key.** The turn, the tilt
// and the fit lived in the pause menu, which is two clicks and a screen away
// from the board they act on — and on a touch screen there was no other way to
// reach any of them. The keys mirror this list; they never extend it.
//
// The model is pure and `ToolRail` draws it, the shape `tool.ts` set: what is
// offered and what is lit is arithmetic a test can hold.

import type { Tilt } from "src/client/ai/render/cameraRig";
import type { IconName } from "./kit";

/** One tile of the camera group, ready to draw. */
export interface ViewAction {
  /** The `data-testid`, and it is API. */
  id: "view-rotate-back" | "view-rotate" | "view-tilt" | "view-fit";
  label: string;
  icon: IconName;
  /**
   * The accelerator that runs it, lower-cased.
   *
   * **The letter is written here and nowhere else**: `useCameraKeys` looks the
   * press up in this list rather than keeping an `if` chain of its own, and the
   * hint's suffix is built from it — so a rebinding cannot leave a tile
   * advertising a key that does nothing.
   */
  key: string;
  /** What it does and the key that does it, said once. */
  hint: string;
  /** A toggle's state, where it has one. `aria-pressed` is drawn from it. */
  on?: boolean;
}

export interface ViewInput {
  /** Which pitch the camera is on — the tilt tile is the only reader. */
  tilt: Tilt;
}

/**
 * The four tiles, in the order they hang under the tools.
 *
 * Anticlockwise before clockwise, so the pair reads left-to-right the way the
 * board turns; the tilt and the fit after them because they are answers to
 * "where am I" rather than to "which way round".
 */
export function viewActions({ tilt }: ViewInput): ViewAction[] {
  /** A row before its key is spelled into its hint. */
  type Row = Omit<ViewAction, "hint"> & { does: string };

  const rows: Row[] = [
    {
      id: "view-rotate-back",
      label: "Turn left",
      icon: "rotateLeft",
      key: "q",
      does: "45° anticlockwise"
    },
    {
      id: "view-rotate",
      label: "Turn right",
      icon: "rotateRight",
      key: "e",
      does: "45° clockwise"
    },
    {
      id: "view-tilt",
      label: "Tilt",
      icon: "tiltShift",
      key: "t",
      does: "table view or top-down",
      // The only toggle in the group: it is lit while the board is being seen
      // from straight above, which is a state you can be left in.
      on: tilt === "top"
    },
    {
      id: "view-fit",
      label: "Fit",
      icon: "fitScreen",
      key: "f",
      does: "the whole board on screen"
    }
  ];

  return rows.map(({ does, ...row }) => ({
    ...row,
    hint: `${does} · ${row.key.toUpperCase()}`
  }));
}
